import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { 
  BarChart2, TrendingUp, Users, FileText, Award, Target, 
  AlertTriangle, CheckCircle, ArrowLeft, Filter, RefreshCw,
  BookOpen, GraduationCap, ChevronDown, ChevronRight, List,
  Layers, Eye, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const AO_COLORS = { AO1: '#6366f1', AO2: '#10b981', AO3: '#f59e0b' };

const TeacherAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { examId } = useParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // View mode: 'overview' or 'exam'
  const [viewMode, setViewMode] = useState(examId ? 'exam' : 'overview');
  const [selectedExamId, setSelectedExamId] = useState(examId || null);
  
  // Filter state
  const [filters, setFilters] = useState({
    grade_id: '',
    subject_id: '',
    assessment_type_code: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    grades: [],
    subjects: [],
    assessment_types: []
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Analytics data - Overview
  const [overview, setOverview] = useState(null);
  const [aoBreakdown, setAoBreakdown] = useState(null);
  const [assessmentTypePerformance, setAssessmentTypePerformance] = useState(null);
  const [studentPerformance, setStudentPerformance] = useState([]);
  const [trends, setTrends] = useState([]);
  const [examsList, setExamsList] = useState([]);
  const [topicPerformance, setTopicPerformance] = useState([]);
  
  // Analytics data - Exam specific
  const [examAnalytics, setExamAnalytics] = useState(null);

  useEffect(() => {
    fetchFilterOptions();
    if (examId) {
      setViewMode('exam');
      setSelectedExamId(examId);
      fetchExamAnalytics(examId);
    } else {
      fetchAllAnalytics();
    }
  }, [examId]);

  useEffect(() => {
    if (!loading && viewMode === 'overview') {
      fetchAllAnalytics();
    }
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/teacher/analytics/filters');
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const fetchAllAnalytics = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filters.grade_id) params.append('grade_id', filters.grade_id);
      if (filters.subject_id) params.append('subject_id', filters.subject_id);
      if (filters.assessment_type_code) params.append('assessment_type_code', filters.assessment_type_code);
      
      const queryString = params.toString();
      const baseQuery = queryString ? '?' + queryString : '';
      const separator = queryString ? '&' : '?';
      
      const [overviewRes, aoRes, atRes, studentsRes, trendsRes, examsRes, topicsRes] = await Promise.all([
        api.get(`/teacher/analytics/overview${baseQuery}`),
        api.get(`/teacher/analytics/ao-breakdown${baseQuery}`),
        api.get(`/teacher/analytics/assessment-type-performance${baseQuery}`),
        api.get(`/teacher/analytics/student-performance${baseQuery}${separator}limit=20`),
        api.get(`/teacher/analytics/trends${baseQuery}${separator}months=6`),
        api.get(`/teacher/analytics/exams${baseQuery}`),
        api.get(`/teacher/analytics/topic-performance${baseQuery}`)
      ]);
      
      setOverview(overviewRes.data);
      setAoBreakdown(aoRes.data);
      setAssessmentTypePerformance(atRes.data.assessment_types);
      setStudentPerformance(studentsRes.data.students);
      setTrends(trendsRes.data.trends);
      setExamsList(examsRes.data.exams);
      setTopicPerformance(topicsRes.data.topics);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchExamAnalytics = async (eId) => {
    setRefreshing(true);
    try {
      const response = await api.get(`/teacher/analytics/exam/${eId}`);
      setExamAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch exam analytics:', err);
      toast.error('Failed to load exam analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExamSelect = (eId) => {
    setSelectedExamId(eId);
    setViewMode('exam');
    setLoading(true);
    fetchExamAnalytics(eId);
    navigate(`/teacher/analytics/${eId}`);
  };

  const handleBackToOverview = () => {
    setViewMode('overview');
    setSelectedExamId(null);
    setExamAnalytics(null);
    setLoading(true);
    fetchAllAnalytics();
    navigate('/teacher/analytics');
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A*': 'bg-purple-500', 'A': 'bg-indigo-500', 'B': 'bg-blue-500',
      'C': 'bg-green-500', 'D': 'bg-yellow-500', 'E': 'bg-orange-500',
      'F': 'bg-red-400', 'G': 'bg-red-500', 'U': 'bg-red-600'
    };
    return colors[grade] || 'bg-gray-400';
  };

  const getSeverityColor = (severity) => {
    return severity === 'critical' ? 'text-red-600 bg-red-50' : 
           severity === 'moderate' ? 'text-amber-600 bg-amber-50' : 
           'text-yellow-600 bg-yellow-50';
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  // Prepare chart data
  const gradeDistributionData = overview?.grade_distribution 
    ? Object.entries(overview.grade_distribution)
        .map(([grade, count]) => ({ grade, count }))
        .filter(d => d.count > 0)
    : [];

  const aoChartData = aoBreakdown?.ao_performance
    ? Object.entries(aoBreakdown.ao_performance).map(([key, data]) => ({
        ao: key,
        name: data.name,
        percentage: data.percentage,
        fullMark: 100
      }))
    : [];

  const assessmentTypeData = assessmentTypePerformance
    ? Object.entries(assessmentTypePerformance).map(([code, data]) => ({
        code,
        name: data.name,
        percentage: data.average_percentage,
        exams: data.exam_count,
        submissions: data.submission_count
      }))
    : [];

  // Exam-specific chart data
  const examGradeDistData = examAnalytics?.grade_distribution
    ? Object.entries(examAnalytics.grade_distribution)
        .map(([grade, count]) => ({ grade, count }))
        .filter(d => d.count > 0)
    : [];

  const examAoChartData = examAnalytics?.ao_performance
    ? Object.entries(examAnalytics.ao_performance).map(([key, data]) => ({
        ao: key,
        name: data.name,
        percentage: data.percentage,
        fullMark: 100
      }))
    : [];

  const questionPerfData = examAnalytics?.question_performance || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // EXAM-SPECIFIC VIEW
  if (viewMode === 'exam' && examAnalytics) {
    const { exam, summary, grade_distribution, ao_performance, question_performance, student_rankings } = examAnalytics;
    
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <button 
              onClick={handleBackToOverview}
              className="flex items-center gap-2 text-purple-200 hover:text-white mb-4 transition-colors"
              data-testid="back-to-overview-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Overview
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <FileText className="w-8 h-8" />
                  {exam.title}
                </h1>
                <div className="mt-2 flex items-center gap-4 text-purple-200">
                  <span>{exam.subject_name}</span>
                  <span>•</span>
                  <span>{exam.grade_name}</span>
                  <span>•</span>
                  <span>{exam.assessment_type_name || exam.assessment_type_code}</span>
                  <span>•</span>
                  <span>{exam.total_questions} Questions</span>
                </div>
              </div>
              <button
                onClick={() => fetchExamAnalytics(selectedExamId)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Submissions</p>
              <p className="text-2xl font-bold text-slate-900">{summary.total_submissions}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Graded</p>
              <p className="text-2xl font-bold text-slate-900">{summary.graded_submissions}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Average</p>
              <p className={`text-2xl font-bold ${getPerformanceColor(summary.average_percentage)}`}>
                {summary.average_percentage}%
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Pass Rate</p>
              <p className={`text-2xl font-bold ${summary.pass_rate >= 70 ? 'text-green-600' : summary.pass_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                {summary.pass_rate}%
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3 text-green-500" /> Highest
              </p>
              <p className="text-2xl font-bold text-green-600">{summary.highest_score}%</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                <ArrowDownRight className="w-3 h-3 text-red-500" /> Lowest
              </p>
              <p className="text-2xl font-bold text-red-600">{summary.lowest_score}%</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* AO Performance */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                AO Performance
              </h2>
              
              {examAoChartData.length > 0 ? (
                <>
                  <div className="h-52 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={examAoChartData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="ao" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="Performance %" dataKey="percentage" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.5} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(ao_performance || {}).map(([key, data]) => (
                      <div key={key} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: AO_COLORS[key] }}></div>
                        <div className="flex-1 flex items-center justify-between">
                          <span className="text-sm text-slate-700">{key}: {data.name}</span>
                          <span className="text-sm font-bold" style={{ color: AO_COLORS[key] }}>{data.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-52 flex items-center justify-center text-slate-400">
                  <p>No AO data available</p>
                </div>
              )}
            </div>

            {/* Grade Distribution */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" />
                Grade Distribution
              </h2>
              
              {examGradeDistData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={examGradeDistData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="grade" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  <p>No grade data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Question-wise Performance */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-green-600" />
              Question-wise Performance
            </h2>
            
            {questionPerfData.length > 0 ? (
              <div className="h-64 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={questionPerfData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="question_idx" type="category" width={50} tickFormatter={(v) => `Q${v + 1}`} />
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, 'Avg Score']}
                      labelFormatter={(label) => `Question ${label + 1}`}
                    />
                    <Bar dataKey="avg_percentage" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <p>No question performance data available</p>
              </div>
            )}
            
            {/* Question Details Table */}
            {questionPerfData.length > 0 && (
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-600">Q#</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-600">Question</th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-600">Type</th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-600">Marks</th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-600">Avg %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {questionPerfData.map((q, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium">Q{q.question_idx + 1}</td>
                        <td className="px-4 py-2 text-slate-600 max-w-xs truncate">{q.question_text}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs ${q.question_type === 'mcq' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            {q.question_type.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">{q.total_marks}</td>
                        <td className={`px-4 py-2 text-center font-bold ${getPerformanceColor(q.avg_percentage)}`}>
                          {q.avg_percentage}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Student Rankings */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Student Rankings
              </h2>
            </div>
            
            {student_rankings?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Rank</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Student</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Marks</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">%</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Grade</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">AO1</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">AO2</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">AO3</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {student_rankings.map((student, idx) => (
                      <tr key={student.student_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <span className={`w-8 h-8 inline-flex items-center justify-center rounded-full text-sm font-bold ${
                            idx === 0 ? 'bg-amber-100 text-amber-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-slate-900">{student.student_name}</td>
                        <td className="px-6 py-4 text-center text-slate-600">{student.marks_obtained}/{student.max_marks}</td>
                        <td className={`px-6 py-4 text-center font-bold ${getPerformanceColor(student.percentage)}`}>
                          {student.percentage}%
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-bold text-white ${getGradeColor(student.grade)}`}>
                            {student.grade}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center text-sm" style={{ color: AO_COLORS.AO1 }}>
                          {student.ao_breakdown?.AO1 || 0}%
                        </td>
                        <td className="px-6 py-4 text-center text-sm" style={{ color: AO_COLORS.AO2 }}>
                          {student.ao_breakdown?.AO2 || 0}%
                        </td>
                        <td className="px-6 py-4 text-center text-sm" style={{ color: AO_COLORS.AO3 }}>
                          {student.ao_breakdown?.AO3 || 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No student data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // OVERVIEW VIEW
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/teacher/dashboard')}
            className="flex items-center gap-2 text-indigo-200 hover:text-white mb-4 transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <BarChart2 className="w-8 h-8" />
                Class Analytics
              </h1>
              <p className="mt-2 text-indigo-200">Performance metrics and AO gap analysis</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  showFilters ? 'bg-white text-indigo-700' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid="filters-btn"
              >
                <Filter className="w-4 h-4" />
                Filters
                {(filters.grade_id || filters.subject_id || filters.assessment_type_code) && (
                  <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                )}
              </button>
              <button
                onClick={fetchAllAnalytics}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
          
          {/* Filters */}
          {showFilters && (
            <div className="mt-6 p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-indigo-200 mb-1">Grade/Class</label>
                  <select
                    value={filters.grade_id}
                    onChange={(e) => setFilters(prev => ({ ...prev, grade_id: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg bg-white text-slate-800 border-0 focus:ring-2 focus:ring-indigo-300"
                    data-testid="filter-grade"
                  >
                    <option value="">All Grades</option>
                    {filterOptions.grades.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-indigo-200 mb-1">Subject</label>
                  <select
                    value={filters.subject_id}
                    onChange={(e) => setFilters(prev => ({ ...prev, subject_id: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg bg-white text-slate-800 border-0 focus:ring-2 focus:ring-indigo-300"
                    data-testid="filter-subject"
                  >
                    <option value="">All Subjects</option>
                    {filterOptions.subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-indigo-200 mb-1">Assessment Type</label>
                  <select
                    value={filters.assessment_type_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, assessment_type_code: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg bg-white text-slate-800 border-0 focus:ring-2 focus:ring-indigo-300"
                    data-testid="filter-assessment-type"
                  >
                    <option value="">All Types</option>
                    {filterOptions.assessment_types.map(at => (
                      <option key={at.code} value={at.code}>{at.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {(filters.grade_id || filters.subject_id || filters.assessment_type_code) && (
                <button
                  onClick={() => setFilters({ grade_id: '', subject_id: '', assessment_type_code: '' })}
                  className="mt-3 text-sm text-indigo-200 hover:text-white"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-sm text-slate-600">Total Exams</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{overview?.total_exams || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-slate-600">Students</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{overview?.total_students || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-600">Avg Score</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{overview?.average_percentage || 0}%</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-slate-600">Graded</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              {overview?.graded_submissions || 0}
              <span className="text-lg text-slate-400">/{overview?.total_submissions || 0}</span>
            </p>
          </div>
        </div>

        {/* Exam List for Drill-down */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <List className="w-5 h-5 text-indigo-600" />
            Exams (Click to view detailed analytics)
          </h2>
          
          {examsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Exam</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Grade</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Submissions</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Avg %</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {examsList.map((exam) => (
                    <tr key={exam.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => handleExamSelect(exam.id)}>
                      <td className="px-4 py-3 font-medium text-slate-900">{exam.title}</td>
                      <td className="px-4 py-3 text-slate-600">{exam.subject_name}</td>
                      <td className="px-4 py-3 text-slate-600">{exam.grade_name}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">
                          {exam.assessment_type_code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {exam.graded_submissions}/{exam.total_submissions}
                      </td>
                      <td className={`px-4 py-3 text-center font-bold ${getPerformanceColor(exam.average_percentage)}`}>
                        {exam.average_percentage}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleExamSelect(exam.id); }}
                          className="p-2 rounded-lg hover:bg-indigo-100 text-indigo-600 transition-colors"
                          data-testid={`view-exam-${exam.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No exams found</p>
              <p className="text-sm">Create and grade exams to see analytics</p>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* AO Gap Analysis */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              AO Performance Analysis
            </h2>
            
            {aoChartData.length > 0 ? (
              <>
                <div className="h-64 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={aoChartData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="ao" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar
                        name="Performance %"
                        dataKey="percentage"
                        stroke="#6366f1"
                        fill="#6366f1"
                        fillOpacity={0.5}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-3">
                  {Object.entries(aoBreakdown?.ao_performance || {}).map(([key, data]) => (
                    <div key={key} className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: AO_COLORS[key] }}
                      ></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{key}: {data.name}</span>
                          <span className="text-sm font-bold" style={{ color: AO_COLORS[key] }}>
                            {data.percentage}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ width: `${data.percentage}%`, backgroundColor: AO_COLORS[key] }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No AO data available</p>
                  <p className="text-sm">Grade submissions to see AO breakdown</p>
                </div>
              </div>
            )}
          </div>

          {/* Gap Analysis & Recommendations */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Learning Gaps & Recommendations
            </h2>
            
            {aoBreakdown?.gap_analysis?.length > 0 ? (
              <div className="space-y-4">
                {aoBreakdown.gap_analysis.map((gap, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg ${getSeverityColor(gap.severity)}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{gap.ao}: {gap.name}</span>
                      <span className="text-sm font-bold">{gap.percentage}%</span>
                    </div>
                    <div className="text-sm opacity-80">
                      Severity: <span className="capitalize font-medium">{gap.severity}</span>
                    </div>
                  </div>
                ))}
                
                {aoBreakdown?.recommendations?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Recommendations</h3>
                    <div className="space-y-3">
                      {aoBreakdown.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-900">{rec.area}</p>
                            <p className="text-sm text-blue-700">{rec.suggestion}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p className="text-green-600 font-medium">No significant gaps detected!</p>
                  <p className="text-sm">Students are performing well across all AOs</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Grade Distribution */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Grade Distribution
            </h2>
            
            {gradeDistributionData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={gradeDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="grade" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Award className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No grade data available</p>
                </div>
              </div>
            )}
          </div>

          {/* Assessment Type Performance */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-green-600" />
              Performance by Assessment Type
            </h2>
            
            {assessmentTypeData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={assessmentTypeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, 'Avg Score']}
                      labelFormatter={(label) => `Assessment: ${label}`}
                    />
                    <Bar dataKey="percentage" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <GraduationCap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No assessment data available</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Topic-wise Performance */}
        {topicPerformance.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-teal-600" />
              Topic-wise Performance
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {topicPerformance.map((topic, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-lg border ${topic.average_percentage < 50 ? 'border-red-200 bg-red-50' : topic.average_percentage < 70 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-900">{topic.topic}</span>
                    <span className={`text-lg font-bold ${getPerformanceColor(topic.average_percentage)}`}>
                      {topic.average_percentage}%
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 mb-3">
                    {topic.submission_count} submissions • Score: {topic.total_score}/{topic.total_max}
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span style={{ color: AO_COLORS.AO1 }}>AO1: {topic.ao_breakdown.AO1}%</span>
                    <span style={{ color: AO_COLORS.AO2 }}>AO2: {topic.ao_breakdown.AO2}%</span>
                    <span style={{ color: AO_COLORS.AO3 }}>AO3: {topic.ao_breakdown.AO3}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Trends */}
        {trends.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Performance Trends (Last 6 Months)
            </h2>
            
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="average_percentage" 
                    name="Overall" 
                    stroke="#6366f1" 
                    strokeWidth={2}
                    dot={{ fill: '#6366f1' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ao1_percentage" 
                    name="AO1" 
                    stroke="#6366f1" 
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ao2_percentage" 
                    name="AO2" 
                    stroke="#10b981" 
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ao3_percentage" 
                    name="AO3" 
                    stroke="#f59e0b" 
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Student Performance Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Student Performance (Top 20)
            </h2>
          </div>
          
          {studentPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Student</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Submissions</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Avg %</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Grade</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">AO1</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">AO2</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">AO3</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {studentPerformance.map((student, idx) => (
                    <tr key={student.student_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <span className={`w-8 h-8 inline-flex items-center justify-center rounded-full text-sm font-bold ${
                          idx === 0 ? 'bg-amber-100 text-amber-700' :
                          idx === 1 ? 'bg-slate-200 text-slate-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">{student.name}</td>
                      <td className="px-6 py-4 text-center text-slate-600">{student.submissions}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`font-bold ${getPerformanceColor(student.average_percentage)}`}>
                          {student.average_percentage}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold text-white ${getGradeColor(student.most_common_grade)}`}>
                          {student.most_common_grade}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm" style={{ color: AO_COLORS.AO1 }}>
                        {student.ao_breakdown?.AO1 || 0}%
                      </td>
                      <td className="px-6 py-4 text-center text-sm" style={{ color: AO_COLORS.AO2 }}>
                        {student.ao_breakdown?.AO2 || 0}%
                      </td>
                      <td className="px-6 py-4 text-center text-sm" style={{ color: AO_COLORS.AO3 }}>
                        {student.ao_breakdown?.AO3 || 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No student performance data available</p>
              <p className="text-sm">Grade submissions to see student analytics</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherAnalyticsDashboard;
